import { initRawSchema, type RawSchema } from '@principle-theorem/editor';
import {
  FailedDestinationEntityRecord,
  IDestinationEntityJobRunOptions,
  IDestinationEntityRecord,
  ITranslationMap,
  type IDestinationEntity,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { SterilisationRecord } from '@principle-theorem/principle-core';
import {
  type IPatient,
  type IPractice,
  type IStaffer,
  type ISterilisationCycle,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  WithPrimaryKey,
  getError,
  resolveSequentially,
  snapshotCombineLatest,
  toNamedDocument,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, isEmpty } from 'lodash';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { STERILISATION_CYCLE_RESOURCE_TYPE } from '../../../destination/entities/sterilisation-cycle';
import {
  BasePatientSterilisationDestinationEntity,
  ISterilisationRecordBaseJobData,
  ISterilisationRecordMigrationData,
} from '../../../destination/entities/sterilisation-records';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  CorePracticePatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { PROVIDER_RESOURCE_TYPE } from '../../source/entities/providers';
import { getSterilisationCycleKey } from '../../source/entities/sterilisation-cycles';
import {
  ICorePracticeSterilisationRecord,
  SterilisationRecordSourceEntity,
} from '../../source/entities/sterilisation-records';
import { CorePracticePracticeMappingHandler } from '../mappings/practices';
import { CorePracticeStafferMappingHandler } from '../mappings/staff';
import { PatientDestinationEntity } from './patients';
import { StafferDestinationEntity } from './staff';

interface IPatientSterilisationJobData
  extends ISterilisationRecordBaseJobData<CorePracticePatient> {
  practices: WithPrimaryKey<ITranslationMap<IPractice>>[];
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
}

export class PatientSterilisationDestinationEntity extends BasePatientSterilisationDestinationEntity<
  CorePracticePatient,
  IPatientSterilisationJobData
> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    sterilisationRecords: new SterilisationRecordSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
  };

  customMappings = {
    staff: new CorePracticeStafferMappingHandler(),
    practices: new CorePracticePracticeMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientSterilisationJobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(PROVIDER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const practices$ = this.customMappings.practices.getRecords(translationMap);

    return snapshotCombineLatest([staff$, practices$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [staff, practices]]) =>
        sourcePatients.map((sourcePatient) => ({
          sourcePatient,
          staff,
          practices,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientSterilisationJobData
  ): Promise<
    | ISterilisationRecordMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients.getSourceRecordId(
        data.sourcePatient.data
      );
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId.toString(),
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient,
          `Couldn't resolve patient ${sourcePatientId}`
        );
      }

      const patient = await Firestore.getDoc(patientRef);

      const sterilisationRecords = await resolveSequentially(
        await this.sourceEntities.sterilisationRecords.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        ),
        async (record) => {
          const practiceId = record.data.locationId.toString();
          const practiceMap = data.practices.find(
            (searchPractice) =>
              searchPractice.sourceIdentifier === practiceId.toString()
          );

          if (!practiceMap?.destinationIdentifier) {
            throw new Error(`Can't find practice with id ${practiceId}`);
          }
          const practice = await Firestore.getDoc(
            practiceMap?.destinationIdentifier
          );
          const cycle = await getSterilisationCycle(
            record.data,
            translationMap
          );

          return {
            ...SterilisationRecord.init({
              data: record.data.barcode,
              practice: toNamedDocument(practice),
              patient: toNamedDocument(patient),
              cycle: cycle ? toNamedDocument(cycle) : undefined,
              notes: getSterilisationNotes(record.data, !!cycle),
            }),
            sourceIdentifier: record.record.uid,
            createdAt:
              record.record.translations.cycleDateUtc ??
              record.record.translations.completedDateUtc ??
              toTimestamp(),
          };
        }
      );

      return {
        sterilisationRecords,
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }
}

async function getSterilisationCycle(
  record: ICorePracticeSterilisationRecord,
  translationMap: TranslationMapHandler
): Promise<WithRef<ISterilisationCycle> | undefined> {
  const steriliser = record.steriliser?.trim();
  const cycleNumber = record.cycleNumber?.trim();
  const runDateUtc = record.cycleDateUtc?.trim();
  if (!steriliser || !cycleNumber || !runDateUtc) {
    return;
  }
  const cycleMapping = await translationMap.getBySource<ISterilisationCycle>(
    getSterilisationCycleKey(steriliser, cycleNumber, runDateUtc),
    STERILISATION_CYCLE_RESOURCE_TYPE
  );
  if (!cycleMapping?.destinationIdentifier) {
    return;
  }
  return Firestore.getDoc(cycleMapping.destinationIdentifier);
}

function getSterilisationNotes(
  record: ICorePracticeSterilisationRecord,
  hasCycle: boolean
): RawSchema | undefined {
  const cycleLines = hasCycle
    ? []
    : [
        record.cycleNumber?.trim() && `Cycle: ${record.cycleNumber.trim()}`,
        record.steriliser?.trim() && `Steriliser: ${record.steriliser.trim()}`,
      ];
  const lines = compact([record.note?.trim(), ...cycleLines]);
  return isEmpty(lines) ? undefined : initRawSchema(lines);
}
